"use client";

import React from "react";

import { cn, withRef } from "@udecode/cn";
import {
	PlateElement,
	useElement,
	//useRemoveNodeButton,
} from "@udecode/plate-common";
import {
	//ELEMENT_COLUMN,
	ELEMENT_COLUMN_GROUP,
	//useColumnState,
	//useDebouncePopoverOpen,
} from "@udecode/plate-layout";

import { useEditorRef } from "@udecode/plate-core";
import { removeNodes } from "@udecode/slate";
import type { TElement } from "@udecode/slate";
import { findNodePath } from "@udecode/slate-react";

export const useRemoveNodeButton = ({ element }: { element: TElement }) => {
	const editor = useEditorRef();

	return {
		props: {
			onClick: () => {
				const path = findNodePath(editor, element);
				removeNodes(editor, { at: path });
			},
			onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => {
				e.preventDefault();
			},
		},
	};
};

import { useColumnState } from "./column-store";

import type { TColumnGroupElement } from "@udecode/plate-layout";

import { Icons } from "../icons/icons";

import { Button } from "./Button";
import { Popover, PopoverAnchor, PopoverContent } from "./Popover";
import { Separator } from "./Separator";

import { useEditorSelector } from "@udecode/plate-common";
import { isCollapsed } from "@udecode/plate-common";
import { useReadOnly, useSelected } from "slate-react";

export const useDebouncePopoverOpen = () => {
	const readOnly = useReadOnly();
	const selected = useSelected();

	const selectionCollapsed = useEditorSelector(
		(editor) => isCollapsed(editor.selection),
		[]
	);

	// TODO:should add debounce
	return !readOnly && selected && selectionCollapsed;
};

export const ColumnGroupElement = withRef<typeof PlateElement>(
	({ children, className, ...props }, ref) => {
		const element = useElement<TColumnGroupElement>(ELEMENT_COLUMN_GROUP);

		return (
			<PlateElement className={cn(className, "my-2")} ref={ref} {...props}>
				<ColumnFloatingToolbar element={element}>
					<div className={cn("flex size-full gap-4 rounded")}>{children}</div>
				</ColumnFloatingToolbar>
			</PlateElement>
		);
	}
);

export function ColumnFloatingToolbar({
	children,
	element,
}: { element: TColumnGroupElement } & React.PropsWithChildren) {
	const readOnly = useReadOnly();

	const {
		setDoubleColumn,
		setDoubleSideDoubleColumn,
		setLeftSideDoubleColumn,
		setRightSideDoubleColumn,
		setThreeColumn,
	} = useColumnState();

	//const element = useElement<TColumnElement>(ELEMENT_COLUMN);

	const { props: buttonProps } = useRemoveNodeButton({ element });

	const isOpen = useDebouncePopoverOpen();

	if (readOnly) return <>{children}</>;

	return (
		<Popover modal={false} open={isOpen}>
			<PopoverAnchor>{children}</PopoverAnchor>
			<PopoverContent
				align="center"
				className="w-auto p-1"
				onOpenAutoFocus={(e: any) => e.preventDefault()}
				side="top"
				sideOffset={10}
			>
				<div className="box-content flex h-9 items-center gap-1 [&_svg]:size-4 [&_svg]:text-muted-foreground">
					<Button onClick={setDoubleColumn} size="sms" variant="ghost">
						<Icons.doubleColumn />
					</Button>
					<Button onClick={setThreeColumn} size="sms" variant="ghost">
						<Icons.threeColumn />
					</Button>
					<Button onClick={setRightSideDoubleColumn} size="sms" variant="ghost">
						<Icons.rightSideDoubleColumn />
					</Button>
					<Button onClick={setLeftSideDoubleColumn} size="sms" variant="ghost">
						<Icons.leftSideDoubleColumn />
					</Button>
					<Button
						onClick={setDoubleSideDoubleColumn}
						size="sms"
						variant="ghost"
					>
						<Icons.doubleSideDoubleColumn />
					</Button>

					<Separator className="my-1" orientation="vertical" />
					<Button size="sms" variant="ghost" {...buttonProps}>
						<Icons.delete />
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}
